import { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Mail, MapPin, Landmark, Receipt, Palette, MapPinned, MessageSquare,
  CheckCircle2, Circle, Save, Loader2, Upload, Image as ImageIcon, Info, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const ADMIN_ROLES = ["superadmin", "platform_admin", "directeur", "beheerder"];
const WRITE_ROLES = ["superadmin", "platform_admin", "directeur", "beheerder"];

const TABS = [
  { key: "organisatie", label: "Organisatie", icon: Building2 },
  { key: "contact", label: "Contact", icon: Mail },
  { key: "adressen", label: "Adressen", icon: MapPin },
  { key: "bank", label: "Bankgegevens", icon: Landmark },
  { key: "facturatie", label: "Facturatie", icon: Receipt },
  { key: "huisstijl", label: "Huisstijl", icon: Palette },
  { key: "locaties", label: "Locaties", icon: MapPinned },
  { key: "communicatie", label: "Communicatie", icon: MessageSquare },
  { key: "ai_schriftje", label: "AI-schriftje", icon: Sparkles },
  { key: "voortgang", label: "Voortgang", icon: CheckCircle2 },
];

// ------- Helper: field with dirty tracking -------
function useDirtyForm(initial) {
  const [form, setForm] = useState(initial || {});
  const [dirty, setDirty] = useState(false);
  const set = useCallback((k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setDirty(true);
  }, []);
  const reset = useCallback((v) => { setForm(v || {}); setDirty(false); }, []);
  return { form, set, reset, dirty };
}

function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid ? `${testid}-field` : undefined}>
      <Label className="text-xs font-medium text-neutral-700">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-neutral-500 mt-1">{hint}</p>}
    </div>
  );
}

function SectionCard({ title, description, children, testid }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4" data-testid={testid}>
      <div>
        <h2 className="font-display text-xl tracking-tight text-neutral-900">{title}</h2>
        {description && <p className="text-sm text-neutral-500 mt-1">{description}</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

// -------------------- TABS --------------------
function OrganisatieTab({ org, saveOrg, canWrite }) {
  const { form, set, reset, dirty } = useDirtyForm(org);
  useEffect(() => reset(org), [org, reset]);
  return (
    <div className="space-y-4">
      <SectionCard title="Organisatiegegevens" description="Officiële en fiscale identiteit" testid="section-organisatie">
        <Field label="Officiële statutaire naam *" testid="statutaire_naam">
          <Input value={form.statutaire_naam || ""} onChange={e => set("statutaire_naam", e.target.value)} data-testid="input-statutaire_naam" />
        </Field>
        <Field label="Handelsnaam" testid="handelsnaam">
          <Input value={form.handelsnaam || ""} onChange={e => set("handelsnaam", e.target.value)} data-testid="input-handelsnaam" />
        </Field>
        <Field label="Rechtsvorm">
          <Select value={form.rechtsvorm || ""} onValueChange={v => set("rechtsvorm", v)}>
            <SelectTrigger data-testid="input-rechtsvorm"><SelectValue placeholder="Kies…" /></SelectTrigger>
            <SelectContent>
              {["BV", "Stichting", "Vereniging", "Eenmanszaak", "VOF", "Coöperatie", "Overig"].map(v =>
                <SelectItem key={v} value={v}>{v}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </Field>
        <Field label="KVK-nummer (8 cijfers)" testid="kvk">
          <Input value={form.kvk || ""} onChange={e => set("kvk", e.target.value.replace(/\D/g, "").slice(0, 8))} data-testid="input-kvk" />
        </Field>
        <Field label="Vestigingsnummer" testid="vestigingsnummer">
          <Input value={form.vestigingsnummer || ""} onChange={e => set("vestigingsnummer", e.target.value)} data-testid="input-vestigingsnummer" />
        </Field>
        <Field label="RSIN">
          <Input value={form.rsin || ""} onChange={e => set("rsin", e.target.value)} data-testid="input-rsin" />
        </Field>
        <Field label="Btw-nummer" testid="btw_nummer">
          <Input value={form.btw_nummer || ""} onChange={e => set("btw_nummer", e.target.value)} data-testid="input-btw_nummer" placeholder="NL123456789B01" />
        </Field>
        <Field label="Btw-status">
          <Select value={form.btw_status || ""} onValueChange={v => set("btw_status", v)}>
            <SelectTrigger data-testid="input-btw_status"><SelectValue placeholder="Kies…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="belastingplichtig">Belastingplichtig</SelectItem>
              <SelectItem value="vrijgesteld">Vrijgesteld</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {form.btw_status === "vrijgesteld" && (
          <Field label="Tekst btw-vrijstelling voor facturen">
            <Textarea value={form.btw_vrijstelling_tekst || ""}
              onChange={e => set("btw_vrijstelling_tekst", e.target.value)}
              placeholder="Bijv. Vrijgesteld van btw op grond van art. 11 lid 1 letter w Wet OB."
              rows={2} data-testid="input-btw_vrijstelling_tekst" />
          </Field>
        )}
        <Field label="Land">
          <Input value={form.land || "NL"} onChange={e => set("land", e.target.value.toUpperCase())} maxLength={2} data-testid="input-land" />
        </Field>
        <Field label="Standaardvaluta">
          <Input value={form.valuta || "EUR"} onChange={e => set("valuta", e.target.value.toUpperCase())} maxLength={3} data-testid="input-valuta" />
        </Field>
        <Field label="Standaardtaal">
          <Input value={form.taal || "nl"} onChange={e => set("taal", e.target.value.toLowerCase())} maxLength={2} data-testid="input-taal" />
        </Field>
        <Field label="Website">
          <Input value={form.website || ""} onChange={e => set("website", e.target.value)} data-testid="input-website" placeholder="https://…" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Bedrijfsomschrijving" hint="Kort algemene omschrijving">
            <Textarea rows={2} value={form.beschrijving || ""} onChange={e => set("beschrijving", e.target.value)} data-testid="input-beschrijving" />
          </Field>
        </div>
      </SectionCard>
      <SaveBar dirty={dirty} onSave={() => saveOrg(form).then(() => reset(form))} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}

function ContactTab({ org, saveOrg, canWrite }) {
  const { form, set, reset, dirty } = useDirtyForm(org);
  useEffect(() => reset(org), [org, reset]);
  return (
    <div className="space-y-4">
      <SectionCard title="Contactgegevens" description="E-mailadressen mogen dezelfde zijn — één algemeen adres volstaat" testid="section-contact">
        <Field label="Algemeen e-mailadres">
          <Input value={form.email || ""} onChange={e => set("email", e.target.value)} data-testid="input-email" />
        </Field>
        <Field label="Telefoonnummer">
          <Input value={form.telefoon || ""} onChange={e => set("telefoon", e.target.value)} data-testid="input-telefoon" />
        </Field>
        <Field label="Mobiel">
          <Input value={form.mobiel || ""} onChange={e => set("mobiel", e.target.value)} data-testid="input-mobiel" />
        </Field>
        <Field label="Website">
          <Input value={form.website || ""} onChange={e => set("website", e.target.value)} data-testid="input-website2" />
        </Field>
        <Field label="Administratie e-mail">
          <Input value={form.email_administratie || ""} onChange={e => set("email_administratie", e.target.value)} data-testid="input-email_administratie" />
        </Field>
        <Field label="Facturatie e-mail">
          <Input value={form.email_facturatie || ""} onChange={e => set("email_facturatie", e.target.value)} data-testid="input-email_facturatie" />
        </Field>
        <Field label="Directie e-mail">
          <Input value={form.email_directie || ""} onChange={e => set("email_directie", e.target.value)} data-testid="input-email_directie" />
        </Field>
        <Field label="Ouder-communicatie e-mail">
          <Input value={form.email_ouder || ""} onChange={e => set("email_ouder", e.target.value)} data-testid="input-email_ouder" />
        </Field>
        <Field label="Personeel e-mail">
          <Input value={form.email_personeel || ""} onChange={e => set("email_personeel", e.target.value)} data-testid="input-email_personeel" />
        </Field>
        <Field label="Reply-to e-mail">
          <Input value={form.email_reply_to || ""} onChange={e => set("email_reply_to", e.target.value)} data-testid="input-email_reply_to" />
        </Field>
        <Field label="Directeur naam">
          <Input value={form.directeur_naam || ""} onChange={e => set("directeur_naam", e.target.value)} data-testid="input-directeur_naam" />
        </Field>
        <Field label="Contactpersoon naam">
          <Input value={form.contactpersoon_naam || ""} onChange={e => set("contactpersoon_naam", e.target.value)} data-testid="input-contactpersoon_naam" />
        </Field>
        <Field label="Functie contactpersoon">
          <Input value={form.contactpersoon_functie || ""} onChange={e => set("contactpersoon_functie", e.target.value)} data-testid="input-contactpersoon_functie" />
        </Field>
        <Field label="Telefoon contactpersoon">
          <Input value={form.contactpersoon_telefoon || ""} onChange={e => set("contactpersoon_telefoon", e.target.value)} data-testid="input-contactpersoon_telefoon" />
        </Field>
      </SectionCard>
      <SaveBar dirty={dirty} onSave={() => saveOrg(form).then(() => reset(form))} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}

function AddressBlock({ prefix, title, addr, onChange, testid }) {
  const setF = (k, v) => onChange({ ...(addr || {}), [k]: v });
  return (
    <div className="md:col-span-2 border-t border-neutral-200 pt-4 first:border-0 first:pt-0" data-testid={testid}>
      <h3 className="font-medium text-neutral-800 mb-3">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-3"><Field label="Straat"><Input value={(addr || {}).straat || ""} onChange={e => setF("straat", e.target.value)} data-testid={`${prefix}-straat`} /></Field></div>
        <div><Field label="Nummer"><Input value={(addr || {}).huisnr || ""} onChange={e => setF("huisnr", e.target.value)} data-testid={`${prefix}-huisnr`} /></Field></div>
        <div><Field label="Toev."><Input value={(addr || {}).toevoeging || ""} onChange={e => setF("toevoeging", e.target.value)} data-testid={`${prefix}-toev`} /></Field></div>
        <div><Field label="Postcode"><Input value={(addr || {}).postcode || ""} onChange={e => setF("postcode", e.target.value)} data-testid={`${prefix}-postcode`} placeholder="1234 AB" /></Field></div>
        <div className="md:col-span-3"><Field label="Plaats"><Input value={(addr || {}).plaats || ""} onChange={e => setF("plaats", e.target.value)} data-testid={`${prefix}-plaats`} /></Field></div>
        <div className="md:col-span-2"><Field label="Provincie"><Input value={(addr || {}).provincie || ""} onChange={e => setF("provincie", e.target.value)} data-testid={`${prefix}-provincie`} /></Field></div>
        <div><Field label="Land"><Input value={(addr || {}).land || "NL"} onChange={e => setF("land", e.target.value)} maxLength={2} data-testid={`${prefix}-land`} /></Field></div>
      </div>
    </div>
  );
}

function AdressenTab({ org, saveOrg, canWrite }) {
  const { form, set, reset, dirty } = useDirtyForm(org);
  useEffect(() => reset(org), [org, reset]);
  return (
    <div className="space-y-4">
      <SectionCard title="Adressen" description="Bezoek-, post-, factuur- en correspondentieadres" testid="section-adressen">
        <div className="md:col-span-2 flex items-center gap-3">
          <Switch checked={form.gebruik_bezoekadres_als_postadres ?? true}
            onCheckedChange={v => set("gebruik_bezoekadres_als_postadres", v)}
            data-testid="input-gebruik_bezoekadres_als_postadres" />
          <div className="text-sm">Gebruik bezoekadres ook als post- en factuuradres</div>
        </div>
        <AddressBlock prefix="visit" title="Bezoekadres (vestigingsadres)" addr={form.vestigingsadres}
          onChange={v => set("vestigingsadres", v)} testid="addr-vestiging" />
        {!(form.gebruik_bezoekadres_als_postadres ?? true) && (
          <>
            <AddressBlock prefix="post" title="Postadres" addr={form.postadres}
              onChange={v => set("postadres", v)} testid="addr-post" />
            <AddressBlock prefix="factuur" title="Factuuradres" addr={form.factuuradres}
              onChange={v => set("factuuradres", v)} testid="addr-factuur" />
            <AddressBlock prefix="corr" title="Correspondentieadres" addr={form.correspondentieadres}
              onChange={v => set("correspondentieadres", v)} testid="addr-correspondentie" />
          </>
        )}
      </SectionCard>
      <SaveBar dirty={dirty} onSave={() => saveOrg(form).then(() => reset(form))} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}

// -------- Bank + Facturatie: gebruikt company_entity, niet organization --------
function BankTab({ orgId, canWrite }) {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const { form, set, reset, dirty } = useDirtyForm({});

  useEffect(() => {
    api.get(`/organizations/${orgId}/entities`).then(res => {
      const items = res.data?.items || [];
      const first = items[0] || null;
      setEntity(first);
      reset(first || {});
    }).catch(() => setEntity(null)).finally(() => setLoading(false));
  }, [orgId, reset]);

  const create = async () => {
    if (!form.juridische_naam) return toast.error("Juridische naam is verplicht");
    try {
      const res = await api.post(`/organizations/${orgId}/entities`, form);
      setEntity(res.data);
      reset(res.data);
      toast.success("Bank-/facturatiegegevens aangemaakt");
    } catch (e) { toast.error(e.response?.data?.detail || "Kon niet opslaan"); }
  };

  const save = async () => {
    if (!entity) return create();
    try {
      const res = await api.patch(`/organizations/${orgId}/entities/${entity.id}`, form);
      setEntity(res.data);
      reset(res.data);
      toast.success("Bankgegevens bijgewerkt (audit-gelogd)");
    } catch (e) { toast.error(e.response?.data?.detail || "Kon niet opslaan"); }
  };

  if (loading) return <div className="text-sm text-neutral-500 p-6">Laden…</div>;

  return (
    <div className="space-y-4">
      <SectionCard title="Bankgegevens" description="IBAN-wijzigingen worden gelogd in de auditgeschiedenis. Volledig zichtbaar voor directie/beheerder." testid="section-bank">
        <Field label="Rekeninghouder">
          <Input value={form.rekeninghouder || ""} onChange={e => set("rekeninghouder", e.target.value)} data-testid="input-rekeninghouder" />
        </Field>
        <Field label="IBAN" hint="Wordt gevalideerd + spaties verwijderd">
          <Input value={form.iban || ""} onChange={e => set("iban", e.target.value.toUpperCase())} data-testid="input-iban" placeholder="NL91ABNA0417164300" />
        </Field>
        <Field label="BIC/SWIFT">
          <Input value={form.bic || ""} onChange={e => set("bic", e.target.value)} data-testid="input-bic" />
        </Field>
        <Field label="Juridische naam (op factuur)">
          <Input value={form.juridische_naam || ""} onChange={e => set("juridische_naam", e.target.value)} data-testid="input-juridische_naam" />
        </Field>
        <Field label="Handelsnaam op factuur">
          <Input value={form.handelsnaam || ""} onChange={e => set("handelsnaam", e.target.value)} data-testid="input-entity-handelsnaam" />
        </Field>
        <Field label="Standaard betalingsomschrijving">
          <Input value={form.betaal_omschrijving || ""} onChange={e => set("betaal_omschrijving", e.target.value)} data-testid="input-betaal_omschrijving" placeholder="Factuur {factuurnummer}" />
        </Field>
      </SectionCard>
      <SaveBar dirty={dirty} onSave={save} canWrite={canWrite} lastUpdated={entity?.updated_at} />
    </div>
  );
}

function FactuurTab({ org, orgId, saveOrg, canWrite }) {
  const [entity, setEntity] = useState(null);
  const orgForm = useDirtyForm(org);
  const entityForm = useDirtyForm({});

  useEffect(() => { orgForm.reset(org); /* eslint-disable-next-line */ }, [org]);
  useEffect(() => {
    api.get(`/organizations/${orgId}/entities`).then(res => {
      const first = (res.data?.items || [])[0] || null;
      setEntity(first);
      entityForm.reset(first || {});
    }).catch(() => {});
    // eslint-disable-next-line
  }, [orgId]);

  const saveBoth = async () => {
    // Save org
    if (orgForm.dirty) await saveOrg(orgForm.form);
    if (entity && entityForm.dirty) {
      try {
        const res = await api.patch(`/organizations/${orgId}/entities/${entity.id}`, entityForm.form);
        setEntity(res.data);
        entityForm.reset(res.data);
        toast.success("Factuur-instellingen bijgewerkt");
      } catch (e) { toast.error(e.response?.data?.detail || "Kon niet opslaan"); }
    }
  };

  const dirty = orgForm.dirty || entityForm.dirty;

  return (
    <div className="space-y-4">
      <SectionCard title="Factuurnummering" description="Historische definitieve facturen behouden hun originele nummer" testid="section-factuur-nummers">
        <Field label="Factuurnummer-formaat" hint="Placeholders: {jaar} {seq:04d}">
          <Input value={entityForm.form.factuur_nr_format || "{jaar}-{seq:04d}"}
            onChange={e => entityForm.set("factuur_nr_format", e.target.value)}
            data-testid="input-factuur_nr_format" />
        </Field>
        <Field label="Startnummer">
          <Input type="number" value={entityForm.form.factuur_nr_start ?? 1}
            onChange={e => entityForm.set("factuur_nr_start", parseInt(e.target.value || "1"))}
            data-testid="input-factuur_nr_start" />
        </Field>
        <Field label="Standaard betaaltermijn (dagen)">
          <Input type="number" value={entityForm.form.betaal_termijn_dagen ?? 14}
            onChange={e => entityForm.set("betaal_termijn_dagen", parseInt(e.target.value || "14"))}
            data-testid="input-betaal_termijn_dagen" />
        </Field>
        <Field label="Standaard betaal-methode">
          <Select value={entityForm.form.betaal_methode || "incasso"} onValueChange={v => entityForm.set("betaal_methode", v)}>
            <SelectTrigger data-testid="input-betaal_methode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="incasso">Automatische incasso</SelectItem>
              <SelectItem value="overschrijving">Overschrijving</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </SectionCard>

      <SectionCard title="Factuurinhoud" description="Standaardteksten die onderaan iedere factuur staan" testid="section-factuur-inhoud">
        <div className="md:col-span-2">
          <Field label="Standaard factuurvoet">
            <Textarea rows={2} value={orgForm.form.factuur_voettekst || ""}
              onChange={e => orgForm.set("factuur_voettekst", e.target.value)}
              data-testid="input-factuur_voettekst"
              placeholder="Bedankt voor uw vertrouwen — {organisatie_naam}" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Juridische tekst / voorwaarden">
            <Textarea rows={2} value={orgForm.form.factuur_juridische_tekst || ""}
              onChange={e => orgForm.set("factuur_juridische_tekst", e.target.value)}
              data-testid="input-factuur_juridische_tekst" />
          </Field>
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <Switch checked={orgForm.form.factuur_toon_bank ?? true}
            onCheckedChange={v => orgForm.set("factuur_toon_bank", v)}
            data-testid="input-factuur_toon_bank" />
          <div className="text-sm">Bankgegevens tonen op factuur-PDF</div>
        </div>
      </SectionCard>

      <SaveBar dirty={dirty} onSave={saveBoth} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}

function HuisstijlTab({ org, orgId, saveOrg, canWrite }) {
  const { form, set, reset, dirty } = useDirtyForm(org);
  const [uploading, setUploading] = useState(false);
  useEffect(() => reset(org), [org, reset]);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Bestand te groot (max 5 MB)");
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(file.type)) return toast.error("Alleen PNG/JPG/SVG/WEBP");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/organizations/${orgId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.logo_url;
      if (url) {
        set("logo_url", url);
        await saveOrg({ logo_url: url });
        toast.success("Logo geüpload");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Upload mislukt"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Logo & huisstijl" description="Wordt gebruikt op app-header, facturen, e-mails en publieke inschrijfpagina" testid="section-huisstijl">
        <div className="md:col-span-2 flex items-start gap-6" data-testid="logo-block">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-neutral-200 flex items-center justify-center bg-neutral-50 overflow-hidden">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
            ) : (
              <ImageIcon className="h-12 w-12 text-neutral-300" strokeWidth={1.5} />
            )}
          </div>
          <div>
            <p className="text-sm text-neutral-700 mb-2">Primair logo (PNG/JPG/SVG, max 5 MB)</p>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded-md text-sm cursor-pointer hover:bg-neutral-50" data-testid="logo-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Bezig…" : "Logo uploaden"}
                <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={e => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
              </label>
              {form.logo_url && (
                <Button variant="outline" size="sm" onClick={() => { set("logo_url", null); saveOrg({ logo_url: null }); }} data-testid="logo-remove-btn">
                  Verwijderen
                </Button>
              )}
            </div>
          </div>
        </div>

        <Field label="Primaire huisstijlkleur" hint="Hex-code bv. #2f5b46">
          <Input type="color" value={form.kleur_primair || "#2f5b46"} onChange={e => set("kleur_primair", e.target.value)} data-testid="input-kleur_primair" className="w-24 h-10 p-1" />
        </Field>
        <Field label="Secundaire kleur">
          <Input type="color" value={form.kleur_secundair || "#e9dfce"} onChange={e => set("kleur_secundair", e.target.value)} data-testid="input-kleur_secundair" className="w-24 h-10 p-1" />
        </Field>
        <Field label="Accentkleur">
          <Input type="color" value={form.kleur_accent || "#c86f5b"} onChange={e => set("kleur_accent", e.target.value)} data-testid="input-kleur_accent" className="w-24 h-10 p-1" />
        </Field>
        <Field label="Ouderapp-naam" hint="Zoals ouders het in hun app zien">
          <Input value={form.ouderapp_naam || ""} onChange={e => set("ouderapp_naam", e.target.value)} data-testid="input-ouderapp_naam" />
        </Field>
      </SectionCard>
      <SaveBar dirty={dirty} onSave={() => saveOrg(form).then(() => reset(form))} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}
function AISchriftjeTab({ org, saveOrg, canWrite }) {
  const { form, set, reset, dirty } = useDirtyForm(org);
  useEffect(() => reset(org), [org, reset]);
  return (
    <div className="space-y-4">
      <SectionCard title="AI-schriftje voorkeuren" description="Bepaalt hoe de AI-assistent schriftjes schrijft voor deze KDV" testid="section-ai-schriftje">
        <Field label="Verhaal-toon" hint="Bepaalt tone-of-voice van gegenereerde schriftjes">
          <Select value={form.ai_schriftje_toon || "warm"} onValueChange={v => set("ai_schriftje_toon", v)}>
            <SelectTrigger data-testid="input-ai_schriftje_toon"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="warm">Warm & persoonlijk (aanbevolen)</SelectItem>
              <SelectItem value="zakelijk">Zakelijk & feitelijk</SelectItem>
              <SelectItem value="speels">Speels & luchtig</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="md:col-span-2 flex items-center gap-3 pt-3">
          <Switch checked={form.ai_schriftje_toon_leerdoel ?? true}
            onCheckedChange={v => set("ai_schriftje_toon_leerdoel", v)}
            data-testid="input-ai_schriftje_toon_leerdoel" />
          <div className="text-sm">
            Voeg <em>"Wat we vandaag hebben geleerd"</em> — pedagogisch inzicht onderaan elk schriftje
          </div>
        </div>
      </SectionCard>
      <SaveBar dirty={dirty} onSave={() => saveOrg(form).then(() => reset(form))} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}



function CommunicatieTab({ org, saveOrg, canWrite }) {
  const { form, set, reset, dirty } = useDirtyForm(org);
  useEffect(() => reset(org), [org, reset]);
  return (
    <div className="space-y-4">
      <SectionCard title="Communicatie-instellingen" description="E-mail afzender-identiteit en handtekening" testid="section-communicatie">
        <Field label="Afzendernaam">
          <Input value={form.afzendernaam || ""} onChange={e => set("afzendernaam", e.target.value)} data-testid="input-afzendernaam" />
        </Field>
        <Field label="Reply-to e-mailadres">
          <Input value={form.email_reply_to || ""} onChange={e => set("email_reply_to", e.target.value)} data-testid="input-email_reply_to2" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Standaard e-mailhandtekening">
            <Textarea rows={4} value={form.email_handtekening || ""} onChange={e => set("email_handtekening", e.target.value)}
              data-testid="input-email_handtekening"
              placeholder="Met vriendelijke groet,&#10;{directeur_naam}&#10;{organisatie_naam}" />
          </Field>
        </div>
      </SectionCard>
      <SaveBar dirty={dirty} onSave={() => saveOrg(form).then(() => reset(form))} canWrite={canWrite} lastUpdated={org?.updated_at} />
    </div>
  );
}

function LocatiesTab() {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="section-locaties">
      <h2 className="font-display text-xl tracking-tight text-neutral-900">Locaties</h2>
      <p className="text-sm text-neutral-500 mt-1">
        Locatiebeheer heeft een eigen module met openingstijden, groepen en capaciteit.
      </p>
      <a href="/app/instellingen" className="mt-4 inline-block text-sm text-[color:var(--sage)] hover:underline" data-testid="link-locaties">
        → Ga naar Locatiebeheer
      </a>
    </div>
  );
}

function VoortgangTab({ orgId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/organization-settings/mine/completeness`).then(r => setData(r.data)).catch(() => {});
  }, [orgId]);
  if (!data) return <div className="text-sm text-neutral-500 p-6">Laden…</div>;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="section-voortgang">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-[color:var(--sage-100)] flex items-center justify-center">
          <span className="text-2xl font-display font-semibold text-[color:var(--sage)]" data-testid="voortgang-percent">
            {data.percent}%
          </span>
        </div>
        <div>
          <h2 className="font-display text-xl tracking-tight">Voortgang instellingen</h2>
          <p className="text-sm text-neutral-500">
            {data.done} van {data.total} onderdelen zijn compleet ingesteld
          </p>
        </div>
      </div>
      <ul className="space-y-2" data-testid="voortgang-checklist">
        {data.checklist.map(c => (
          <li key={c.key} className="flex items-center gap-3 py-2 border-b border-neutral-100 last:border-0" data-testid={`voortgang-item-${c.key}`}>
            {c.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-neutral-300" />}
            <span className={c.done ? "text-neutral-800" : "text-neutral-500"}>{c.label}</span>
            {!c.done && <span className="ml-auto text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Nog invullen</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SaveBar({ dirty, onSave, canWrite, lastUpdated }) {
  return (
    <div className="sticky bottom-4 z-10 mt-4" data-testid="save-bar">
      <div className={`bg-white border rounded-xl px-4 py-3 flex items-center justify-between shadow-sm ${dirty ? "border-amber-300" : "border-neutral-200"}`}>
        <div className="text-xs text-neutral-500">
          {dirty ? (
            <span className="text-amber-700 font-medium" data-testid="save-bar-dirty">● Niet-opgeslagen wijzigingen</span>
          ) : lastUpdated ? (
            <>Laatst opgeslagen: {new Date(lastUpdated).toLocaleString("nl-NL")}</>
          ) : (
            "Nog geen wijzigingen"
          )}
        </div>
        <Button disabled={!dirty || !canWrite} onClick={onSave} data-testid="save-btn">
          <Save className="h-4 w-4 mr-2" />
          {canWrite ? "Wijzigingen opslaan" : "Alleen-lezen"}
        </Button>
      </div>
    </div>
  );
}

// ---------------- Main page ----------------
export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("organisatie");
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const canWrite = useMemo(() => user && WRITE_ROLES.includes(user.role), [user]);

  const fetchOrg = useCallback(async () => {
    try {
      const res = await api.get("/organization-settings/mine");
      setOrg(res.data);
    } catch (e) {
      toast.error("Kon organisatie niet laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && ADMIN_ROLES.includes(user.role)) fetchOrg();
  }, [user, fetchOrg]);

  const saveOrg = async (patch) => {
    try {
      const res = await api.patch("/organization-settings/mine", patch);
      setOrg(res.data);
      toast.success("Instellingen opgeslagen");
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon niet opslaan");
      throw e;
    }
  };

  if (user && !ADMIN_ROLES.includes(user.role)) {
    return <Navigate to="/app" replace />;
  }
  if (loading || !org) {
    return <div className="p-6 text-neutral-500">Instellingen laden…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl tracking-tight text-neutral-900">Organisatie-instellingen</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Alle bedrijfs-, bank-, huisstijl- en communicatiegegevens op één centrale plek —
          gebruikt door facturen, contracten, e-mails, ouderapp en publieke inschrijfpagina.
        </p>
      </div>

      <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-900" data-testid="settings-info">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Wijzigingen worden direct doorgevoerd op nieuwe facturen, contracten en documenten.
          Historisch definitieve facturen behouden hun oorspronkelijke gegevens.
          Iedere wijziging wordt gelogd in de auditgeschiedenis.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Side tabs */}
        <nav className="lg:sticky lg:top-4 lg:self-start bg-white border border-neutral-200 rounded-xl p-2" data-testid="settings-tabs">
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                  active
                    ? "bg-[color:var(--sage)] text-white"
                    : "text-neutral-700 hover:bg-neutral-50"
                }`}>
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div>
          {tab === "organisatie" && <OrganisatieTab org={org} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "contact" && <ContactTab org={org} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "adressen" && <AdressenTab org={org} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "bank" && <BankTab orgId={org.id} canWrite={canWrite} />}
          {tab === "facturatie" && <FactuurTab org={org} orgId={org.id} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "huisstijl" && <HuisstijlTab org={org} orgId={org.id} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "locaties" && <LocatiesTab />}
          {tab === "communicatie" && <CommunicatieTab org={org} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "ai_schriftje" && <AISchriftjeTab org={org} saveOrg={saveOrg} canWrite={canWrite} />}
          {tab === "voortgang" && <VoortgangTab orgId={org.id} />}
        </div>
      </div>
    </div>
  );
}
